<template lang="pug">
.d-flex.justify-center.fill-width
  v-card.pa-8.white.round-xl.card-border.card-width(outlined)
    v-row(align="center" no-gutters)
      v-col(cols="6")
        .text-md.font-semibold {{ $t('record_payment') }}
        div.mt-2(v-if="totalBalance > 0 && totalByPriceTiers.length > 1")
          template(v-for="(ptier, index) in totalByPriceTiers")
            div.text-sm.font-semibold.neutral500--text(:key="'ttlptier-' + index") {{ ptier.name }}: {{ ptier.price | displayPrice }}
      v-col.text-right
        .text-sm.font-semibold.neutral500--text {{ $t('invoice') }} {{ invoice.rid }}
        .text-pricing.font-semibold.no-wrap {{ invoice.total_amount | displayPrice }}
        .text-sm.font-semibold.neutral500--text.no-wrap(v-if="totalBalance > 0") {{ $t('amount_due') }} {{ totalBalance | displayPrice }}

    div.my-6
      template(v-for="transaction in transactions")
        payment-item(:transaction="transaction" :panels="panels" :selfPayMethods="selfPayMethods" :invoice="invoice" @updated="updateTransaction" @deleted="deleteTransaction(transaction)")

    add-payment-item(v-if="showAddPayment" :panels="panels" :selfPayMethods="selfPayMethods" :leadId="invoice.lead" :invoiceNo="invoice.rid" :registration="invoice.registration" @cancel="showAddPayment = false" @added="addTransaction")

    .text-xs.font-semibold.underline.clickable(v-if="!showAddPayment" @click="showAddPayment = true") {{ $t('add_item', { item: $t('payment').toLowerCase() }) }}
    
    v-row.mt-6(no-gutters)
      v-spacer
      v-col(cols="7")
        template(v-if="allowedToAdjust")
          div(v-if="invoice.invoice_discount !== 0")
            .d-flex.align-center
              .text-sm Adjustment
              v-btn.ml-2(small icon @click="onDeleteAdjustment")
                v-icon(small) mdi-close
              v-spacer
              .text-sm {{ invoice.invoice_discount > 0 ? '-' : '' }} {{ Math.abs(invoice.invoice_discount) | displayPrice }}
          template(v-else)
            payment-adjustment(:totalAmount="invoice.total_amount" :invoice="invoice" @updated="onUpdateAdjustment" :positionTop="transactions.length > 0" attach)
          v-divider.my-2
        v-row.my-2(no-gutters)
          v-col
            payment-tax(:rates="taxRates" :taxRate="invoice.extra_data && invoice.extra_data.tax_rate" :taxAmount="invoice.tax_amount" :invoice="invoice" @updated="updateInvoice")
        template(v-if="Auth('root', 'subscribe_marketing')")
          v-divider.my-2
          template(v-for="offer in invoice.redeems")
            v-row.my-2(no-gutters)
              v-col
                applied-offer(:offerItem="offer" @updated="updateAppliedOffer")
            v-divider.my-2
        v-row.my-2(no-gutters)
          v-col
            .text-sm {{ $t('total') }}
          v-col.text-right
            .text-sm.no-wrap {{ invoice.total_amount | displayPrice }}
        v-divider.my-2
        v-row.my-2(no-gutters)
          v-col
            .text-sm {{ $t('paid') }}
          v-col.text-right
            .text-sm.no-wrap {{ totalPaid | displayPrice }}
        v-divider.my-2
        v-row.my-2(no-gutters)
          v-col
            .text-sm.font-semibold.no-wrap {{ $t('amount_due') }}
          v-col.text-right
            .text-md.font-semibold.no-wrap {{ totalBalance | displayPrice }}
        v-divider.my-2
        v-row.mt-2(no-gutters)
          v-col
            .text-sm.font-semibold.no-wrap {{ $t('amount_to_claim') }}
          v-col.text-right
            .text-md.font-semibold.no-wrap {{ amountToClaim | displayPrice }}
        e-invoice-section.mt-6(:invoice="invoice" :transactions="transactions")

  .dialogs
    delete-payment-adjustment-dialog(v-if="openConfirmDeleteAdjustmentDialog" :open.sync="openConfirmDeleteAdjustmentDialog" @deleted="$emit('updated')")
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import AddPaymentItem from './AddPaymentItem.vue'
import PaymentAdjustment from './PaymentAdjustment.vue'
import PaymentItem from './PaymentItemOld.vue'
import PaymentTax from './PaymentTax.vue'
import DeletePaymentAdjustmentDialog from '@/components/clinic/dialogs/DeletePaymentAdjustmentDialog.vue'
import AppliedOffer from "@/components/clinic/checkout/marketing/AppliedOffer.vue";
import EInvoiceSection from '@/components/clinic/checkout/EInvoiceSection.vue'

export default {
  name: 'PaymentPanelPreview',
  components: { AddPaymentItem, PaymentItem, PaymentAdjustment, PaymentTax, DeletePaymentAdjustmentDialog, AppliedOffer, EInvoiceSection },
  props: {
    invoice: {
      type: Object,
      required: true,
      default: () => {
        return {}
      }
    },
    transactions: {
      type: Array,
      required: true
    }
  },
  data: () => ({
    payments: [
      {
        type: 'Self-pay',
        amount: null
      }
    ],
    openConfirmDeleteAdjustmentDialog: false,
    panels: [],
    selfPayMethods: [],
    showAddPayment: false,
    taxRates: []
  }),
  computed: {
    ...mapState('user', ['user', 'member']),
    ...mapGetters({
      company: 'getCompany',
      isDoctor: 'user/isDoctor',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    totalRedeemed () {
      if (!this.invoice) {
        return 0
      }
      return this.invoice.redeems.reduce((sum, redeem) => {
        sum += redeem.reward.discount_amount
        return sum
      }, 0)
    },
    totalBalance () {
      if (!this.invoice) {
        return 0
      }
      return this.invoice.total_amount - this.totalRedeemed - this.totalPaid
    },
    amountToClaim () {
      if (!this.invoice) {
        return 0
      }
      if (this.transactions.length === 0) {
        return 0
      }
      return this.transactions.reduce((sum, trans) => {
        if (trans.channel === 'panel' && !trans.paid) {
          sum += trans.amount
        }
        return sum
      }, 0)
    },
    isAdmin () {
      return this.member.roles.includes('Admin')
    },
    isAdminOrDoctor () {
      if (this.isAdmin || this.isDoctor) return true
      return false
    },
    allowedToAdjust () {
      if (this.company.genes.find(gene => gene.feature === 'lock_payment_adjustment') && !this.isAdminOrDoctor) return false
      return true
    },
    totalPaid () {
      if (!this.invoice) {
        return 0
      }
      if (this.transactions.length === 0) {
        return 0
      }
      return this.transactions.reduce((sum, trans) => {
        sum += trans.amount
        return sum
      }, 0)
    },
    totalByPriceTiers () {
      if (!this.invoice) {
        return []
      }
      let pricetiers = []
      this.invoice.products.forEach(product => {
        let tierName = product.extra_data?.price_tier_name
        if (tierName) {
          let price = product.price * product.quantity
          let pricetier = pricetiers.find(f => f.name === tierName)
          if (pricetier) {
            pricetier.price += price
          } else {
            pricetiers.push({ name: tierName, price: price })
          }
        }
      })
      return pricetiers
    }
  },
  mounted () {
    this.fetchPanels()
    this.fetchSelfPayMethods()
    this.fetchTaxRates()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchPanels () {
      let params = {
        company: this.isHq ? this.hqSelectedCompany.id : this.company.id,
        channel: 'panel',
        enabled: true,
        fields: 'id,name',
        page_size: -1
      }
      this.$axios.get('/payment_methods', { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.panels = resp.data.results
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    fetchSelfPayMethods () {
      let params = {
        company: this.isHq ? this.hqSelectedCompany.id : this.company.id,
        channel: 'self-pay',
        enabled: true,
        fields: 'id,name',
        page_size: -1
      }
      this.$axios.get('/payment_methods', { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.selfPayMethods = resp.data.results
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    fetchTaxRates () {
      this.$axios.get('/tax_rates?', { params: { company: this.isHq ? this.hqSelectedCompany.id : this.company.id } })
        .then(({ data }) => {
          if (data && data.results) {
            this.taxRates = data.results
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    addTransaction (transaction) {
      this.showAddPayment = false
      this.transactions.push(transaction)
      this.$emit('updated')
    },
    onUpdateAdjustment () {
      this.$emit('updated')
    },
    onDeleteAdjustment () {
      this.openConfirmDeleteAdjustmentDialog = true
    },
    updateTransaction (transaction) {
      if (transaction) {
        const index = this.transactions.findIndex(t => t.uid === transaction.uid)
        this.transactions.splice(index, 1, transaction)
      }
      this.$emit('updated')
    },
    deleteTransaction (transaction) {
      const index = this.transactions.findIndex(t => t.uid === transaction.uid)
      this.transactions.splice(index, 1)
      this.$emit('updated')
    },
    updateInvoice () {
      this.$emit('updated')
    },
    updateAppliedOffer () {
      this.$emit('updatedOffer')
    }
  }
}
</script>

<style lang="scss" scoped>
.card-border {
  border: 1px solid #EEEEEE !important;
}
.fill-width {
  width: 100%;
}
.card-width {
  width: 90%;
}
.text-pricing {
  font-family: $body-font-family;
  font-size: 40px !important;
  line-height: 46px !important;
  letter-spacing: -0.038em !important;
}
</style>